import path from "path";

import { buildActionBegin, ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { IValueProducer } from "../../Commons/TasksCore/BuildTask";
import { ObservableValueProducerBase } from "../../Commons/TasksCore/ChainableValueProducer";
import { ProcessUtils } from "../ProcessUtils/ProcessUtils";
import { ShellProcessHandle } from "../ProcessUtils/ShellProcessHandle";

export class BuildLocalEngineTask extends ObservableValueProducerBase<string> {
    private readonly pathToWebpackProvider: IValueProducer<string>;
    private readonly logger: ILogger;
    private useWatch = false;
    private readonly resourcesDirectory: string;
    private buildProcess?: ShellProcessHandle;
    private readonly buildDebugOnly: boolean;

    public constructor(
        pathToWebpackProvider: IValueProducer<string>,
        resourcesDirectory: string,
        buildDebugOnly: boolean,
        logger?: ILogger
    ) {
        super();
        this.pathToWebpackProvider = pathToWebpackProvider;
        this.resourcesDirectory = resourcesDirectory;
        this.logger = (logger || getLogger()).withPrefix("ENGINE");
        this.buildDebugOnly = buildDebugOnly;
    }

    public async beginListening(): Promise<void> {
        await this.pathToWebpackProvider.beginObserve();
        this.useWatch = true;
    }

    public async stopListening(): Promise<void> {
        this.useWatch = false;
        await this.pathToWebpackProvider.endObserve();
        if (this.buildProcess != undefined) {
            try {
                const buildProcess = this.buildProcess;
                this.buildProcess = undefined;
                await buildProcess.kill();
            } catch (error) {
                this.logger.error(`Failed to stop process: ${error}`);
            }
        }
    }

    public async getCurrentValue(): Promise<string> {
        const webpackConfigFile = await this.pathToWebpackProvider.currentValue;
        if (this.useWatch) {
            this.buildProcess = await this.runWatchProcess(webpackConfigFile);
        } else {
            await this.buildEngineFromSources(webpackConfigFile);
        }
        return webpackConfigFile;
    }

    private async buildEngineFromSources(webpackConfigPath: string): Promise<void> {
        const action = buildActionBegin(this.logger, "Build engine");
        const workingDirectory = path.dirname(webpackConfigPath);
        const buildProcess = ProcessUtils.createShellProcess(
            `npm`,
            [
                "run",
                "runtime:build",
                "--",
                `--env`,
                `resourcesPath=${this.resourcesDirectory}`,
                ...this.getBuildOptions(),
            ],
            workingDirectory
        );
        buildProcess.onStdOut.subscribe(data => this.logger.log(data.trimRight()));
        buildProcess.start();
        const code = await buildProcess.waitForExit();
        if (code !== 0) {
            throw new Error(`Executed with exit code ${code}`);
        }
        action.complete();
    }

    private getBuildOptions(): string[] {
        if (this.buildDebugOnly) {
            return ["--env", "development=true", "--env", "testing=false", "--env", "production=false"];
        }
        return [];
    }

    private async runWatchProcess(webpackConfigPath: string): Promise<ShellProcessHandle> {
        const workingDirectory = path.dirname(webpackConfigPath);

        const result = ProcessUtils.createShellProcess(
            "npm",
            [
                "run",
                "runtime:build",
                "--",
                "--watch",
                `--env`,
                `resourcesPath=${this.resourcesDirectory}`,
                ...this.getBuildOptions(),
            ],
            workingDirectory
        );
        result.onStdOut.subscribe(data => this.logger.log(data.trimRight()));
        this.logger.log("Begin watch");
        result.start();
        return result;
    }
}
